import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useVisitApi from './config';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import InviteLinkDialog from './InviteLinkDialog';

const Container = styled.div``;

const StatusChip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

const STATUS_COLORS = {
  waiting: { bg: '#fff4d6', fg: '#8a6d3b' },
  approved: { bg: '#d9f3ea', fg: '#1d9e74' },
  rejected: { bg: '#fde2e2', fg: '#d64545' },
  canceled: { bg: '#ececec', fg: '#666666' },
  expired: { bg: '#ececec', fg: '#666666' },
};

const toId = (v) => (v && typeof v === 'object' ? v.id : v);
const toIds = (arr) => (arr || []).map(toId).filter(Boolean);

export default function VisitsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);
  const [inviteDialog, setInviteDialog] = useState({ open: false, link: null, smsSent: false });

  const {
    objectSchema, columns: baseColumns, data, fetching, pagination,
    create, update, _delete, cancelVisit, sendInvite, statusLabel,
  } = useVisitApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback(async (row) => {
    try {
      const res = await api.get(`/v1/visits/${row.id}`);
      const v = res.data?.data;
      if (!v) return;
      setSelectedRow({
        ...v,
        hostEmployeeId: v.hostEmployeeId || null,
        gateIds: (v.gates || []).filter((g) => g.gateId).map((g) => g.gateId),
        gateGroupIds: (v.gates || []).filter((g) => g.gateGroupId).map((g) => g.gateGroupId),
      });
      setDrawerOpen(true);
    } catch { /* hata toast'ı interceptor'da */ }
  }, []);

  const handleSave = useCallback(async (formData) => {
    const payload = {
      ...formData,
      hostEmployeeId: toId(formData.hostEmployeeId) || null,
      gateIds: toIds(formData.gateIds),
      gateGroupIds: toIds(formData.gateGroupIds),
    };
    if (selectedRow) {
      const r = await update(selectedRow.id, payload);
      if (r.success) setDrawerOpen(false);
      return r.success;
    }
    const r = await create(payload);
    if (r.success) {
      setDrawerOpen(false);
      if (r.data && !r.data.smsSent && r.data.inviteLink) {
        setInviteDialog({ open: true, link: r.data.inviteLink, smsSent: false });
      } else if (r.data?.smsSent) {
        setInviteDialog({ open: true, link: null, smsSent: true });
      }
    }
    return r.success;
  }, [selectedRow, create, update]);

  const handleSendInvite = useCallback(async (row, trySms) => {
    const res = await sendInvite(row.id, trySms);
    if (res) setInviteDialog({ open: true, link: res.inviteLink || null, smsSent: !!res.smsSent });
  }, [sendInvite]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.visitorFullName });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('visit.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setBusy(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setBusy(false);
  }, [_delete, deleteConfirm.ids]);

  const handleCancelConfirm = useCallback(async () => {
    setBusy(true);
    await cancelVisit(cancelConfirm.id);
    setCancelConfirm({ isOpen: false, id: null, name: '' });
    setBusy(false);
  }, [cancelVisit, cancelConfirm.id]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/visits/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    const details = { type: 'details', label: t('visit.details'), onClick: (row) => history.push(`/visits/${row.id}`) };
    if (!canWrite('VM')) return [details];
    return [
      details,
      { type: 'edit', label: t('visit.edit'), onClick: handleEdit },
      { type: 'publish', label: t('visit.sendSms'), onClick: (row) => handleSendInvite(row, true) },
      { type: 'copy', label: t('visit.showLink'), onClick: (row) => handleSendInvite(row, false) },
      { type: 'divider' },
      { type: 'cancel', label: t('visit.cancelVisit'), onClick: (row) => setCancelConfirm({ isOpen: true, id: row.id, name: row.visitorFullName }) },
      { type: 'delete', label: t('visit.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, history, handleEdit, handleDelete, handleSendInvite]);

  const columns = useMemo(() => {
    const withStatus = baseColumns.map((c) => {
      if (c.name !== 'status') return c;
      return {
        ...c,
        options: {
          ...c.options,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            const effective = row.status === 'approved' && new Date(row.validTo) < new Date()
              ? 'expired'
              : row.status;
            const colors = STATUS_COLORS[effective] || STATUS_COLORS.canceled;
            return <StatusChip $bg={colors.bg} $fg={colors.fg}>{statusLabel(row)}</StatusChip>;
          },
        },
      };
    });
    return [
      ...withStatus,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data, statusLabel]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('visit.edit') : t('visit.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <InviteLinkDialog
        open={inviteDialog.open}
        link={inviteDialog.link}
        smsSent={inviteDialog.smsSent}
        onClose={() => setInviteDialog({ open: false, link: null, smsSent: false })}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('visit.deleteTitle')}
        message={t('visit.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={busy}
      />

      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('visit.cancelTitle')}
        message={t('visit.cancelMessage', { name: cancelConfirm.name })}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      {useMemo(() => (
        <DataTable
          title={t('visit.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('VM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('VM') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
